import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  RiAccountBoxLine,
  RiAddBoxLine,
  RiArrowDropDownLine,
  RiBuildingLine,
  RiDashboardLine,
  RiProfileLine,
  RiSettings3Line,
  RiSuitcaseLine,
  RiLogoutCircleLine,
  RiBillLine,
} from "react-icons/ri";
import { Bell, Book, BookCopy, BookMarkedIcon, Briefcase, BriefcaseBusiness, ChartBarIncreasing, CircleDollarSign, Clock, Cloud, Contact2, ContactRound, FileCheck2, FileEditIcon, FileStack, Flame, Hash, LayoutList, Menu, Rows4, Signature, SquareCheckBig, SquareLibrary, SquarePen } from "lucide-react";
import { ArrowBack, MoreHorizOutlined, ChangeCircleOutlined, CloudDoneOutlined, CloudOutlined, Create, CreateOutlined, Add, AddOutlined, AddCircleOutline, DocumentScannerOutlined, Money, PunchClockOutlined, MoreTimeOutlined, QueryBuilderOutlined, AssessmentRounded, AssessmentOutlined, PermContactCalendarRounded } from "@mui/icons-material";
import Logo from "../resources/logo.png";
import "../styles/Nav.css";
import { Theme } from "./Theme";
import { EnhancedSearch } from "./EnhancedSearch";
import { NotificationTray } from "./NotificationTray";
import { useDocumentGenerationPreferences } from './DocumentGenerationPreferences';
import { canNavigateTimeBilling, TIME_BILLING_PATH } from '../utils/timeBillingNavigation';
import SearchBar from './SearchBar';

export { SearchBar };

/* Hook to detect clicks outside a given ref */
function useOutsideClick(ref, callback) {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [ref, callback]);
}


/* Dropdown Component */
const Dropdown = ({
  label,
  Icon,
  expanded,
  isOpen,
  toggle,
  onItemClick,
  submenuItems,
  activeBase,
  activeRoutes = [],
  location,
  navigate,
  setLowerOption,
}) => {
  const parentClass =
    location.pathname === activeBase
      ? "active"
      : location.pathname.startsWith(activeBase) || activeRoutes.includes(location.pathname)
      ? "main-active"
      : "";
  
  const isActive = (route) => location.pathname === route;
  
  // Filter out falsy values from submenuItems
  const validSubmenuItems = submenuItems.filter(item => item);
  
  return (
    <>
      <div className="menu-item-container">
        <div
          className={`menu-item ${parentClass}`}
          onClick={() => {
            toggle();
            onItemClick();
          }}
          style={{ justifyContent: expanded ? "flex-start" : "center" }}
          title={label}
        >
          <Icon size={25} />
          <span style={{ display: expanded ? "block" : "none" }}>{label}</span>
        </div>
        {validSubmenuItems.length > 0 && expanded && <div className="menu-dropdown">
          <RiArrowDropDownLine
            size={25}
            style={{
              display: expanded ? "block" : "none",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              fill: isOpen ? "var(--fill)" : "var(--text-color)",
            }}
            onClick={toggle}
          />
        </div>}
      </div>
      {isOpen && validSubmenuItems.length > 0 && (
        <div className='submenu'>
          {validSubmenuItems.map((item, index) => {
            return (
              <div
                key={index}
                className={`submenu-item${isActive(item.route) ? " active" : ""}${expanded ? " expanded" : ""}`}
                style={{ justifyContent: expanded ? "flex-start" : "center" }}
                onClick={() => {
                  navigate(item.route);
                  if (activeBase !== "/dashboard" && activeBase !== "/library" && activeBase !== "/cases" && activeBase !== "/reports") {
                    setLowerOption(activeBase.substring(1));
                  }
                }}
              >
                {item.Icon && <item.Icon size={20} />}
                {expanded && <span>{item.label}</span>}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

/* Main options Component */
const MainOptions = ({
  accessLevel,
  expanded,
  lowerOption,
  setLowerOption,
  location,
  navigate,
  dropdowns,
  toggleDropdown,
  mainOptionsRef,
  activeRoute,
  openOption,
  setOpenOption,
}) => {
  const isActive = (route) => location.pathname == route;
  
  return (
    <div id="menu-maininfo" ref={mainOptionsRef}>
      {lowerOption || openOption !== 0 ? (
        <div
          className={`menu-item`}
          style={{ justifyContent: expanded ? "flex-start" : "center" }}
          onClick={() => {setLowerOption(null); setOpenOption(0);}}
          title="More"
        >
          <MoreHorizOutlined size={25} />
          {expanded && <span>More</span>}
        </div>
      ) : (
        <>
          <Dropdown
            label="Dashboard"
            Icon={RiDashboardLine}
            expanded={expanded}
            isOpen={dropdowns.dashboard}
            toggle={() => toggleDropdown("dashboard")}
            onItemClick={() => {
              if (!isActive("/dashboard")) navigate("/dashboard");
            }}
            submenuItems={[
              canNavigateTimeBilling(accessLevel) && {
                label: "Time & Billing",
                route: TIME_BILLING_PATH,
                Icon: Clock,
              },
              accessLevel === 'global admin' && {
                label: "Weather Events",
                route: "/dashboard/weather",
                Icon: Cloud,
                onClick: () => {
                  if (!isActive("/dashboard/weather")) navigate("/dashboard/weather");
                },
              },
            ]}
            activeBase="/dashboard"
            activeRoutes={[TIME_BILLING_PATH]}
            location={location}
            navigate={navigate}
            setLowerOption={setLowerOption}
          />
          {/*<div
            className={`menu-item ${isActive("/cases") ? "active" : ""}`}
            style={{ justifyContent: expanded ? "flex-start" : "center" }}
            onClick={() => {
              if (!isActive("/cases")) navigate("/cases");
            }}
            title="Cases"
          >
            <RiSuitcaseLine size={25} />
            <span style={{ display: expanded ? "block" : "none" }}>
              Cases
            </span>
          </div>
          <div
            className={`menu-item ${isActive("/intake") ? "active" : ""}`}
            style={{ justifyContent: expanded ? "flex-start" : "center" }}
            onClick={() => {
              if (!isActive("/intake")) navigate("/intake");
            }}
            title="Intake"
          >
            <RiAddBoxLine size={25} />
            <span style={{ display: expanded ? "block" : "none" }}>
              Intake
            </span>
          </div>
          <div
            className={`menu-item ${isActive("/contacts") ? "active" : ""}`}
            style={{ justifyContent: expanded ? "flex-start" : "center" }}
            onClick={() => {
              if (!isActive("/contacts")) navigate("/contacts");
            }}
            title="Contacts"
          >
            <RiProfileLine size={25} />
            <span style={{ display: expanded ? "block" : "none" }}>
              Contacts
            </span>
          </div>*/}

          <Dropdown
            label="Cases"
            Icon={BriefcaseBusiness}
            expanded={expanded}
            isOpen={dropdowns.cases}
            toggle={() => toggleDropdown("cases")}
            onItemClick={() => {
              if (!isActive("/cases")) navigate("/cases");
            }}
            submenuItems={[
              {
                label: "Intake",
                route: "/intake",
                Icon: Signature,
                onClick: () => {
                  if (!isActive("/intake")) navigate("/intake");
                },
              },
              {
                label: "Contacts",
                route: "/contacts",
                Icon: ContactRound,
                onClick: () => {
                  if (!isActive("/contacts")) navigate("/contacts");
                },
              },
            ]}
            activeBase="/cases"
            location={location}
            navigate={navigate}
            setLowerOption={setLowerOption}
          />

          <div
            className={`menu-item ${isActive("/library") ? "active" : ""}`}
            style={{ justifyContent: expanded ? "flex-start" : "center" }}
            onClick={() => {
              if (!isActive("/library")) navigate("/library");
            }}
            title="Library"
          >
            <BookCopy size={25} />
            <span style={{ display: expanded ? "block" : "none" }}>
              Library
            </span>
          </div>

          <Dropdown
            label="Reports"
            Icon={LayoutList}
            expanded={expanded}
            isOpen={dropdowns.reports}
            toggle={() => toggleDropdown("reports")}
            onItemClick={() => {
              if (!isActive("/reports")) navigate("/reports");
            }}
            submenuItems={[
              /*{
                label: "Report Builder",
                route: "/reports/builder",
                Icon: SquarePen,
                onClick: () => {
                  if (!isActive("/reports/builder")) navigate("/reports/builder");
                },
              },*/
            ]}
            activeBase="/reports"
            location={location}
            navigate={navigate}
            setLowerOption={setLowerOption}
          />
        </>
      )}
    </div>
  );
};

const SubInfo = ({
  expanded,
  location,
  navigate,
  setLowerOption,
  toggleDropdown,
  dropdowns,
  activeRoute,
  openOption,
  setOpenOption,
}) => {
  const isActive = (route) => location.pathname == route;

  return (
    <div id="menu-subinfo">
      <span className="divider horizontal"></span>
      {openOption === 1 ? (
        <>
          <div
            id="client"
            className={`menu-item ${isActive("/client-portal") ? "active" : ""}`}
            style={{ justifyContent: expanded ? "flex-start" : "center" }}
            onClick={() => {
              if (!isActive("/client-portal")) navigate("/client-portal");
              setLowerOption("client-portal");
            }}
            title="Client Portal"
          >
            <RiAccountBoxLine size={25} />
            <span style={{ display: expanded ? "block" : "none" }}>
              Client Portal
            </span>
          </div>
          {/*<div
            className={`menu-item ${isActive("/ai") ? "active" : ""}`}
            style={{ justifyContent: expanded ? "flex-start" : "center" }}
            onClick={() => {
              if (!isActive("/ai")) navigate("/ai");
              setLowerOption("ai");
            }}
            title="AI"
          >
            <ReviewsOutlinedIcon sx={{ fontSize: 25 }} />
            <span style={{ display: expanded ? "block" : "none" }}>
              AI
            </span>
          </div>*/}
          <Dropdown
            label="Firm Settings"
            Icon={RiBuildingLine}
            expanded={expanded}
            isOpen={dropdowns["firm-settings"]}
            toggle={() => toggleDropdown("firm-settings")}
            onItemClick={() => {
              if (!isActive("/firm-settings")) navigate("/firm-settings");
              setLowerOption("firm-settings");
            }}
            submenuItems={
              /*
              {
                label: "Auto Tags",
                Icon: Hash,
                route: "/firm-settings/auto-tags",
              },
              */
              [
              {
                label: "Billing",
                Icon: CircleDollarSign,
                route: "/firm-settings/0",
              },
              {
                label: "Billing Setup",
                Icon: RiBillLine,
                route: "/firm-settings/1",
              },
              {
                label: "Custom Fields",
                Icon: FileEditIcon,
                route: "/firm-settings/2",
              },
              {
                label: "Layout Editor",
                Icon: ChangeCircleOutlined,
                route: "/firm-settings/3",
              },
              {
                label: 'Task Workflows',
                Icon: SquareCheckBig,
                route: '/firm-settings/5',
              },
              {
                label: 'Team Reassignment',
                Icon: ContactRound,
                route: '/firm-settings/6',
              }
            ]}
            activeBase="/firm-settings"
            location={location}
            navigate={navigate}
            setLowerOption={setLowerOption}
          />
        </>
      ) : (
        <div
          className={`menu-item c more-lower ${expanded ? "open-lower" : ''}`}
          style={{ justifyContent: expanded ? "flex-start" : "center" }}
          onClick={() => setOpenOption(1)}
          title="More"
        >
          <MoreHorizOutlined size={25} />
          {expanded && <span>More</span>}
        </div>
      )}
    </div>
  );
};

/* Footer Component */
const Footer = ({ expanded, notifications, fetchNotifications, navigate, logout, theme, changeTheme, location, accessLevel }) => {
  const {
    reviewBeforeGeneration,
    saving: savingDocumentPreference,
    setReviewBeforeGeneration,
  } = useDocumentGenerationPreferences();
  const [createNew, setCreateNew] = useState(false);
  const [notisOpen, setNotisOpen] = useState(false);
  const bellRef = useRef(null);
  const notiRef = useRef(null);
  const createNewRef = useRef(null);
  const notificationClickTimer = useRef(null);

  const setPageMargins = useCallback(() => {
    document.querySelectorAll(".page-container").forEach((container) => {
      container.style.marginLeft = expanded ? "225x" : "0";
      container.style.transition = "margin 0.3s";
    });
    
    document.querySelectorAll(".case-container").forEach((container) => {
      container.style.marginLeft = expanded ? "225px" : "0";
      container.style.transition = "margin 0.3s";
    });
  }, [expanded]);

  const navigateAndSetMargins = (path) => {
    navigate(path);
    setTimeout(setPageMargins, 100);
  };

  const handleNotificationClick = (e) => {
    e.preventDefault();
    
    // Clear any existing timer
    if (notificationClickTimer.current) {
      clearTimeout(notificationClickTimer.current);
    }

    // Set a timer for single click
    notificationClickTimer.current = setTimeout(() => {
      setNotisOpen(!notisOpen);
      notificationClickTimer.current = null;
    }, 250); // 250ms debounce
  };

  const handleNotificationDoubleClick = (e) => {
    e.preventDefault();
    
    // Clear the single click timer
    if (notificationClickTimer.current) {
      clearTimeout(notificationClickTimer.current);
      notificationClickTimer.current = null;
    }

    // Close notifications tray if open and navigate
    setNotisOpen(false);
    navigate('/notifications');
  };

  const isActive = (route) => location.pathname.startsWith(route);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target) &&
          notiRef.current && !notiRef.current.contains(e.target)) {
        setNotisOpen(false);
      }
    }
    window.addEventListener("click", handleClickOutside);
    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (createNewRef.current && !createNewRef.current.contains(e.target)) {
        setCreateNew(false);
      }
    };

    if (createNew) {
      window.addEventListener("click", handleClickOutside);
    }

    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, [createNew]);

  return (
    <div id="menu-footer" style={{ alignItems: expanded ? "flex-start" : "center" }}>
      <div className="create-new-wrapper" ref={createNewRef}>
        <button className="action nav-toggle-button" onClick={() => {setCreateNew(true); console.log("Create New Clicked")}}>
          <AddCircleOutline />
          <span style={{ display: expanded ? "block" : "none" }}>Create New...</span>
        </button>

        {createNew && (
          <div className="create-new">
            <div className="create-new-item" onClick={() => navigateAndSetMargins("/cases?new=true")}>
              <span>Case</span>
            </div>
            <div className="create-new-item" onClick={() => navigateAndSetMargins("/leads?new=true")}>
              <span>Lead</span>
            </div>
            <div className="create-new-item" onClick={() => navigateAndSetMargins("/contacts?new=true")}>
              <span>Contact</span>
            </div>
            <div className='create-new-item' onClick={() => navigateAndSetMargins("/dashboard?task=true")}>
              <span>Task</span>
            </div>
            <div className='create-new-item' onClick={() => navigateAndSetMargins("/dashboard?note=true")}>
              <span>Note</span>
            </div>
          </div>
        )}
      </div>

      <div className={`menu-item c ${expanded ? "open-lower" : ''}`} ref={bellRef} onClick={handleNotificationClick} onDoubleClick={handleNotificationDoubleClick}>
        <Bell size={25} />
        <div style={{ position: "absolute", right: "10px" }} className='notification priority def'>{notifications?.filter(noti => noti.read_at === null)?.length < 20 ? notifications?.filter(noti => noti.read_at === null)?.length : '20'}</div>
        <span style={{ display: expanded ? "block" : "none" }}>Notifications</span>
      </div>

      {notisOpen && (
          <NotificationTray ref={notiRef} notifications={notifications.filter(noti => noti.read_at === null)} fetchNotifications={fetchNotifications} />
      )}

      {/*<div className={`menu-item c ${expanded ? "open-lower" : ''} ${isActive("/timekeeper") ? "active" : ""}`} onClick={() => !isActive("/timekeeper") && navigateAndSetMargins("/timekeeper")}>
        <QueryBuilderOutlined size={25} />
        <span style={{ display: expanded ? "block" : "none" }}>Timekeeper</span>
      </div>*/}

      <div
        className={`menu-item c ${expanded ? "open-lower" : ''}`}
        onClick={() => {
          if (savingDocumentPreference) return;
          setReviewBeforeGeneration(!reviewBeforeGeneration).catch((error) => {
            console.error('Unable to save document review preference:', error);
          });
        }}
        title={`Document data review is ${reviewBeforeGeneration ? 'on' : 'off'}`}
      >
        <FileCheck2 size={25} />
        <span style={{ display: expanded ? "block" : "none" }}>
          Document Review: {reviewBeforeGeneration ? 'On' : 'Off'}
        </span>
      </div>

      <div
        className={`menu-item c ${expanded ? "open-lower" : ''} ${isActive("/settings") ? "active" : ""}`}
        onClick={() => !isActive("/settings") && navigateAndSetMargins("/settings")}
      >
        <RiSettings3Line size={25} />
        <span style={{ display: expanded ? "block" : "none" }}>Settings</span>
      </div>

      <div className={`menu-item c ${expanded ? "open-lower" : ''}`} onClick={logout}>
        <RiLogoutCircleLine size={25} />
        <span style={{ display: expanded ? "block" : "none" }}>Logout</span>
      </div>

      <Theme theme={theme} changeTheme={changeTheme} />
    </div>
  );
};

/* Main Nav Component */
const Nav = ({ accessLevel, notifications, fetchNotifications, theme, changeTheme, logout }) => {
  const [expanded, setExpanded] = useState(false);
  const [dropdowns, setDropdowns] = useState({
    'dashboard': false,
    'library': false,
    'cases': false,
    "firm-settings": false,
    'reports': false
  });
  const [lowerOption, setLowerOption] = useState(null);
  const [activeRoute, setActiveRoute] = useState("");
  const [openOption, setOpenOption] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const mainOptionsRef = useRef(null);
  const [navClass, setNavClass] = useState("");

  useEffect(() => {
    setOpenOption(lowerOption ? 1 : 0);
  }, [lowerOption]);

  useEffect(() => {
    setActiveRoute(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const updateVh = () => {
      document.documentElement.style.setProperty("--real-vh", `${window.innerHeight}px`);
    };
    updateVh();
    window.addEventListener("resize", updateVh);
    return () => window.removeEventListener("resize", updateVh);
  }, []);

  const setPageMargins = useCallback(() => {
    // Use more efficient CSS classes instead of direct DOM manipulation
    document.body.classList.toggle('nav-expanded', expanded);
  }, [expanded]);

  useEffect(() => {
    setPageMargins();
  }, [expanded, location, setPageMargins]);

  const toggleDropdown = (menu) => {
    setDropdowns((prev) => {
      const newState = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
      newState[menu] = !prev[menu];
      return newState;
    });
  };  

  const isLowerOptionPage = useCallback(() => {
    return (
      location.pathname.startsWith("/client-portal") ||
      location.pathname.startsWith("/firm-settings")
    );
  }, [location.pathname]);

  useEffect(() => {
    if (isLowerOptionPage()) {
      if (location.pathname.startsWith("/client-portal")) {
        setLowerOption("client-portal");
      } else if (location.pathname.startsWith("/firm-settings")) {
        setLowerOption("firm-settings");
      } else {
        setLowerOption(null);
      }
    }
  }, [location.pathname, isLowerOptionPage]);

  useOutsideClick(mainOptionsRef, () => {
    if (!lowerOption && isLowerOptionPage()) {
      if (location.pathname.startsWith("/client-portal"))
        setLowerOption("client-portal");
      else if (location.pathname.startsWith("/firm-settings"))
        setLowerOption("firm-settings");
    }
  });

  return (
    <nav className={`app-navigation ${expanded ? "expanded " : ""}${navClass}`} style={{ width: expanded ? "300px" : "75px" }}>
      <div id="menu">
        <div id="menu-header" style={{ alignItems: expanded ? "flex-start" : "center" }}>
          <div className="nav-toggle" style={{ justifyContent: expanded ? "space-between" : "center" }}>
            <button onClick={() => setExpanded(!expanded)} className='nav-toggle-button'>
              {expanded ? <ArrowBack size={25} /> : <Menu size={25} />}
            </button>
            <img
              src={Logo}
              id="nav-logo"
              alt="Logo"
              style={{
                display: expanded ? "block" : "none",
                filter: theme === "dark" ? "brightness(1000)" : "brightness(0)",
              }}
            />
          </div>
          <EnhancedSearch expanded={expanded} setExpanded={setExpanded} onChange={() => {}} enablePinning />
        </div>
        <span className="divider horizontal"></span>
        <div id="menu-navigation" style={lowerOption || openOption === 1 ? { gap: "15px" } : { justifyContent: "space-between" }}>
          <MainOptions
            accessLevel={accessLevel}
            expanded={expanded}
            lowerOption={lowerOption}
            setLowerOption={setLowerOption}
            location={location}
            navigate={navigate}
            dropdowns={dropdowns}
            toggleDropdown={toggleDropdown}
            mainOptionsRef={mainOptionsRef}
            activeRoute={activeRoute}
            openOption={openOption}
            setOpenOption={setOpenOption}
          />
          {(accessLevel === 'global admin' || accessLevel === 'admin') &&
            <SubInfo
              expanded={expanded}
              location={location}
              navigate={navigate}
              setLowerOption={setLowerOption}
              toggleDropdown={toggleDropdown}
              dropdowns={dropdowns}
              activeRoute={activeRoute}
              openOption={openOption}
              setOpenOption={setOpenOption}
            />
          }
        </div>
      </div>
      <Footer
        expanded={expanded}
        notifications={notifications}
        fetchNotifications={fetchNotifications}
        navigate={navigate}
        logout={logout}
        theme={theme}
        changeTheme={changeTheme}
        location={location}
        activeRoute={activeRoute}
        accessLevel={accessLevel}
      />
    </nav>
  );
};

export default Nav;
